<template>
  <md-card class="container container_40 transfer-confirm">
    <md-progress-bar md-mode="indeterminate" v-if="isPending"/>

    <div class="md-title text_medium">{{ i18n.tr_confirm() }}</div>
      <div class="transfer-confirm__inner">
        <detail-row :prop="i18n.lbl_asset()"
                    :value="`${opts.tokenCode}`"
        />
        <detail-row :prop="i18n.lbl_amount()" :value="`${swimAmount} ${opts.tokenCode}`"/>

        <template>
          <div class="recipient__title">{{ i18n.lbl_recipient() }}</div>
          <md-list class="md-double-line">
            <md-list-item>
              <md-avatar class="md-avatar-icon md-accent">{{ opts.destinationEmail.charAt(0).toUpperCase() }}</md-avatar>

              <div class="md-list-item-text">
                <span>{{ opts.destinationEmail }}</span>
                <span>{{ opts.destinationAccountId }}</span>
              </div>
            </md-list-item>
          </md-list>
        </template>

        <div class="md-layout md-gutter md-alignment-center-space-between transfer-confirm__fees">
          <div class="md-layout-item md-size-45 md-small-size-100">
            <div class="fees__title text_small">{{ i18n.lbl_sender_fees() }}</div>

            <detail-row :prop="i18n.lbl_fixed()" :value="`${senderFee.fixed} ${opts.sourceFeeAsset}`" />
            <detail-row :prop="i18n.lbl_percent()" :value="`${senderFee.percent} ${opts.sourceFeeAsset}`" />
          </div>
          <div class="md-layout-item  md-size-45 md-small-size-100 text-align-right">
            <div class="fees__title text_small">{{ i18n.lbl_recipient_fees() }}</div>
            <detail-row :prop="i18n.lbl_fixed()" :value="`${recipientFee.fixed} ${opts.destinationFeeAsset}`" />
            <detail-row :prop="i18n.lbl_percent()" :value="`${recipientFee.percent} ${opts.destinationFeeAsset}`" />
          </div>
        </div>

        <template>
          <md-checkbox v-model="form.sourcePaysForDest">{{ i18n.tr_pay_fee_for_rec() }}</md-checkbox>
        </template>

        <div v-if="limitsList" class="transfer__limits-container" :class="{ loading: isFeesLoadPending }">
          <template v-for="(item, key) in limitsList" >
            <detail-row
              v-if="item.period === 'day'"
              :prop="i18n.lim_daily_left()"
              :value="(roundCurrency(item.amount_limit - item.amount, opts.tokenCode))" />
            <div v-if="item.period === 'operation' || item.period === 'day'" :key="key" :class="{ 'limits-operational': item.period === 'operation'}">
              <p v-if="item.period === 'operation'">{{ i18n.lim_withdrawal_daily_msg1({ limit: item.amount_limit, asset: opts.tokenCode }) }}</p>
              <p v-if="item.period === 'day'">{{ i18n.lim_withdrawal_daily_msg2({ limit: item.amount_limit, asset: opts.tokenCode }) }}</p>
              <p v-if="key === 0">{{ i18n.lim_withdrawal_daily_msg3() }}</p>
            </div>
          </template>
        </div>

        <h4>{{ i18n.lbl_subject() }}</h4>
        <textarea-field class="transfer-confirm__subject-field" :value="opts.subject" disabled/>
      </div>
    <div class="btn-wrap space-between">
      <md-button class="btn md-primary"
                 @click="$emit(commonEvents.cancelClickEvent)"
                 :disabled="isPending">
        {{ i18n.lbl_cancel() }}
      </md-button>
      <md-button class="btn md-primary"
                 v-if="!amountLessThanFee && !amountGreaterThanBalance"
                 @click="$emit(commonEvents.confirmClickEvent, form.sourcePaysForDest)"
                 :disabled="isPending">
        {{ i18n.lbl_confirm() }}
      </md-button>
    </div>
  </md-card>
</template>

<script>
  import DetailRow from '@vue/app/common/Detail.Row'
  import TextareaField from '@com/fields/TextareaField'

  import { commonEvents } from '@js/events/common_events'
  import { formatDate } from '@utils/dates.util'
  import { assetMap } from '@const'
  import { i18n } from '@js/i18n'
  import { add } from '@utils/math.util'
  import { showAlert } from '@js/modals/alert_message'
  import {isCryptoAsset} from '@utils/consts'
  import CurrencyRound from '@com/mixins/currency.round.mixin'

  export default {
    name: 'transfers-confirm',
    mixins: [CurrencyRound],
    components: { DetailRow, TextareaField },
    props: {
      opts: {type: Object, required: true},
      isPending: {type: Boolean, default: false},
      limitsList: []
    },
    data: _ => ({
      i18n,
      assetMap,
      commonEvents,
      form: {
        sourcePaysForDest: false
      },
      fees: {}
    }),
    created () {
      if (this.checkBalance()) {
        this.checkFee()
      }
      this.feesRound()
    },
    computed: {
      senderFee () {
        if (this.form.sourcePaysForDest) {
          return {
            fixed: this.roundCurrency(add(this.fees.sourceFixed, this.fees.destinationFixed), this.opts.sourceFeeAsset, false),
            percent: this.roundCurrency(add(this.fees.sourcePercent, this.fees.destinationPercent), this.opts.sourceFeeAsset, false)
          }
        }
        return {
          fixed: this.fees.sourceFixed,
          percent: this.fees.sourcePercent
        }
      },
      recipientFee () {
        if (this.form.sourcePaysForDest) {
          return { fixed: this.roundCurrency(0, this.opts.sourceFeeAsset), percent: this.roundCurrency(0, this.opts.sourceFeeAsset) }
        }
        return {
          fixed: this.fees.destinationFixed,
          percent: this.fees.destinationPercent
        }
      },
      amountLessThanFee () {
        return Number(this.senderFee.percent) +
          Number(this.senderFee.fixed) +
          Number(this.recipientFee.percent) +
          Number(this.recipientFee.fixed) >= Number(this.opts.amount)
      },
      amountGreaterThanBalance () {
        return this.$parent.amountGreaterThanBalance(
          this.opts.amount,
          this.senderFee,
          this.recipientFee,
          this.opts.sourceBalance,
          this.form.sourcePaysForDest
        )
      },
      swimAmount () {
        if (isCryptoAsset(this.opts.tokenCode)) {
          return this.roundCurrency2(this.opts.amount, this.opts.tokenCode)
        }
        return this.roundCurrency(this.opts.amount, this.opts.tokenCode)
      }
    },
    methods: {
      formatDate,
      checkBalance () {
        return !this.amountGreaterThanBalance
      },
      checkFee () {
        if (this.amountLessThanFee) {
          showAlert({
            title: i18n.tr_sending_error(),
            message: i18n.tr_less_than_fee()
          })
        }
      },
      feesRound () {
        this.fees = {
          sourceFixed: this.roundCurrency(this.opts.sourceFixedFee, this.opts.sourceFeeAsset, false),
          sourcePercent: this.roundCurrency(this.opts.sourcePercentFee, this.opts.sourceFeeAsset, false),
          destinationFixed: this.roundCurrency(this.opts.destinationFixedFee, this.opts.sourceFeeAsset, false),
          destinationPercent: this.roundCurrency(this.opts.destinationPercentFee, this.opts.sourceFeeAsset, false)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '~scss/variables';
  @import '~scss/mixins';

  .transfer-confirm__subject-field {
    padding-top: 0;
  }

  .transfer-confirm__inner {
    margin-top: 12px;
  }

  .transfer-confirm__fees .detail-row {
    white-space: nowrap;
  }

  .fees__title,
  .recipient__title {
    margin-top: 12px;
    color: $col-text-secondary;
    @include respond-to-custom(medium) {
      text-align: left;
    }
  }
</style>



// WEBPACK FOOTER //
// src/vue/app/transfers/make/Transfers.Confirm.vue